import posthog from 'posthog-js'

export function initializePosthog(apiKey: string) {
  posthog.init(apiKey, {
    api_host: 'https://eu.i.posthog.com', // https://hotdog.byrever.com
    autocapture: false
  })
}

export function setUserPosthog(
  email: string | undefined,
  group: string | undefined
) {
  if (!email) return
  posthog?.identify(email, {
    $email: email,
    $group: group
  })
}

export function setGroupPosthog(group: string | undefined) {
  if (!group) return
  posthog?.group('company', group)
}

// PostHog can't read Flagr, so the hierarchical configuration announcement targets this property.
export function setGroupLevelWarehousesPosthog() {
  posthog?.setPersonProperties({ group_level_warehouses: true })
}

// The same for group-level exchanges settings.
export function setGroupLevelExchangesPosthog() {
  posthog?.setPersonProperties({ group_level_exchanges: true })
}

// The same for group-level return conditions, return reasons or refunds.
export function setGroupLevelReturnsAndRefundsPosthog() {
  posthog?.setPersonProperties({ group_level_returns_refunds: true })
}

export type ExchangesEvent =
  | 'Exchanges viewed'
  | 'Exchanges settings saved'
  | 'Exchanges store settings reset'

export function captureExchangesEvent(
  event: ExchangesEvent,
  properties: { group: string } & Record<string, unknown>
) {
  posthog?.capture(event, properties)
}

export type WarehousesEvent =
  | 'Warehouses viewed'
  | 'Warehouse saved'
  | 'Warehouse deleted'
  | 'Warehouse rules saved'
  | 'Warehouse store rules reset'

export function captureWarehousesEvent(
  event: WarehousesEvent,
  properties: { group: string } & Record<string, unknown>
) {
  posthog?.capture(event, properties)
}

export type ReturnsAndRefundsEvent =
  | 'Returns and refunds viewed'
  | 'Returns and refunds settings saved'
  | 'Returns and refunds store settings reset'

export function captureReturnsAndRefundsEvent(
  event: ReturnsAndRefundsEvent,
  properties: { group: string } & Record<string, unknown>
) {
  posthog?.capture(event, properties)
}

export type EcommerceScope = 'all' | 'group' | 'single'

// "All" is the absence of a selected ecommerce or group, not a value of its own.
export function toEcommerceScope(
  selectedEcommerce?: string,
  selectedGroup?: string
): EcommerceScope {
  if (selectedEcommerce) return 'single'
  return selectedGroup ? 'group' : 'all'
}

// Super-properties: without them "All" is indistinguishable from a
// single-ecommerce view on $pageview and on every other event.
export function registerEcommerceScopePosthog(
  scope: EcommerceScope,
  ecommerceCount: number
) {
  posthog?.register({ ecommerceScope: scope, ecommerceCount })
}

export function captureEcommerceScopeChange(properties: {
  ecommerceScope: EcommerceScope
  fromEcommerceScope: EcommerceScope
  ecommerceId?: string
  groupId?: string
  ecommerceCount: number
  isReverUser: boolean
}) {
  posthog?.capture('Ecommerce scope changed', properties)
}
